// src/components/SpatialNodesView.tsx
import React, {useMemo, useRef, useEffect, useState, useCallback} from 'react';
import {Globe, Users, ChartColumn, TriangleAlert, Fuel, Pickaxe, Trees, Droplet, Flame} from 'lucide-react';
import {useSimulationStore} from '../../store/useSimulationStore';
import KpiCard from '../common/KpiCard';
import StatusBadge, {BadgePreset} from "../common/StatusBadge";
import Dot from '../common/StatusDot';
import { fmtValue } from '../../utils/format';

interface Transform {
   x: number;
   y: number;
   scale: number;
}

const MINIMAP_W = 160;
const MINIMAP_H = 100;
const MIN_SCALE = 0.08;
const MAX_SCALE = 10;

const SpatialNodesView: React.FC = () => {
   const nodes = useSimulationStore(s => s.nodes);
   const connections = useSimulationStore(s => s.connections);
   const setSelectedNodeId = useSimulationStore(s => s.setSelectedNodeId);
   const setSelectedConnectionId = useSimulationStore(s => s.setSelectedConnectionId);
   const resourceNodes = useSimulationStore(s => s.resourceNodes);
   const investments = useSimulationStore(s => s.investments);
   const setSelectedResourceNodeId = useSimulationStore(s => s.setSelectedResourceNodeId);
   
   const svgRef = useRef<SVGSVGElement>(null);
   const containerRef = useRef<HTMLDivElement>(null);
   
   const [dims, setDims] = useState({w: 800, h: 600});
   const [transform, setTransform] = useState<Transform>({x: 0, y: 0, scale: 1});
   const [dragging, setDragging] = useState(false);
   const [dragStart, setDragStart] = useState({x: 0, y: 0});
   const [hoveredNode, setHoveredNode] = useState<number | null>(null);
   const [hoveredConn, setHoveredConn] = useState<number | null>(null);
   const [mousePos, setMousePos] = useState({x: 0, y: 0});
   const [legendOpen, setLegendOpen] = useState(false);
   
   // Track active touches for pinch-zoom
   const touchesRef = useRef<React.Touch[]>([]);
   const lastPinchDist = useRef<number | null>(null);
   
   // ── Container resize ──────────────────────────────────────────────────────────
   useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      const ro = new ResizeObserver(() => setDims({w: el.offsetWidth, h: el.offsetHeight}));
      ro.observe(el);
      setDims({w: el.offsetWidth, h: el.offsetHeight});
      return () => ro.disconnect();
   }, []);
   
   // ── World bounds ──────────────────────────────────────────────────────────────
   const bounds = useMemo(() => {
      if (nodes.length === 0) return {minX: 0, maxX: 1000, minY: 0, maxY: 1000};
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      nodes.forEach(n => {
         minX = Math.min(minX, n.x);
         maxX = Math.max(maxX, n.x);
         minY = Math.min(minY, n.y);
         maxY = Math.max(maxY, n.y);
      });
      const px = (maxX - minX) * 0.15 + 60;
      const py = (maxY - minY) * 0.15 + 60;
      return {minX: minX - px, maxX: maxX + px, minY: minY - py, maxY: maxY + py};
   }, [nodes]);
   
   const worldW = bounds.maxX - bounds.minX;
   const worldH = bounds.maxY - bounds.minY;
   
   // ── Fit to screen ─────────────────────────────────────────────────────────────
   const fitToScreen = useCallback(() => {
      if (dims.w === 0 || dims.h === 0 || worldW === 0 || worldH === 0) return;
      const s = Math.min(dims.w / worldW, dims.h / worldH) * 0.9;
      const x = (dims.w - worldW * s) / 2 - bounds.minX * s;
      const y = (dims.h - worldH * s) / 2 - bounds.minY * s;
      setTransform({x, y, scale: s});
   }, [dims, bounds, worldW, worldH]);
   
   const hasFit = useRef(false);
   useEffect(() => {
      if (dims.w === 0 || dims.h === 0) return;
      if (nodes.length === 0) return;
      if (!hasFit.current) {
         fitToScreen();
         hasFit.current = true;
      }
   }, [dims.w, dims.h, nodes.length, fitToScreen]);
   
   // ── Mouse wheel zoom ──────────────────────────────────────────────────────────
   const handleWheel = useCallback((e: React.WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY > 0 ? 0.84 : 1.19;
      const rect = svgRef.current!.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      setTransform(t => {
         const s = Math.max(MIN_SCALE, Math.min(MAX_SCALE, t.scale * factor));
         const r = s / t.scale;
         return {scale: s, x: mx - r * (mx - t.x), y: my - r * (my - t.y)};
      });
   }, []);
   
   // ── Mouse pan ─────────────────────────────────────────────────────────────────
   const onMouseDown = useCallback((e: React.MouseEvent) => {
      if (e.button !== 0) return;
      setDragging(true);
      setDragStart({x: e.clientX - transform.x, y: e.clientY - transform.y});
   }, [transform]);
   
   const onMouseMove = useCallback((e: React.MouseEvent) => {
      setMousePos({x: e.clientX, y: e.clientY});
      if (!dragging) return;
      setTransform(t => ({...t, x: e.clientX - dragStart.x, y: e.clientY - dragStart.y}));
   }, [dragging, dragStart]);
   
   const onMouseUp = useCallback(() => setDragging(false), []);
   
   // ── Touch handlers ────────────────────────────────────────────────────────────
   const onTouchStart = useCallback((e: React.TouchEvent) => {
      // Don't prevent default on elements that need click events (handled per element)
      touchesRef.current = Array.from(e.touches) as unknown as React.Touch[];
      lastPinchDist.current = null;
      
      if (e.touches.length === 1) {
         const t = e.touches[0];
         setDragging(true);
         setDragStart({x: t.clientX - transform.x, y: t.clientY - transform.y});
      }
   }, [transform]);
   
   const onTouchMove = useCallback((e: React.TouchEvent) => {
      e.preventDefault(); // prevent page scroll while panning/zooming map
      
      if (e.touches.length === 1 && dragging) {
         // Single finger — pan
         const t = e.touches[0];
         setMousePos({x: t.clientX, y: t.clientY});
         setTransform(tr => ({...tr, x: t.clientX - dragStart.x, y: t.clientY - dragStart.y}));
         
      } else if (e.touches.length === 2) {
         // Two fingers — pinch zoom
         setDragging(false);
         const t0 = e.touches[0];
         const t1 = e.touches[1];
         const dist = Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
         const midX = (t0.clientX + t1.clientX) / 2;
         const midY = (t0.clientY + t1.clientY) / 2;
         
         if (lastPinchDist.current !== null) {
            const factor = dist / lastPinchDist.current;
            const rect = svgRef.current?.getBoundingClientRect();
            if (rect) {
               const mx = midX - rect.left;
               const my = midY - rect.top;
               setTransform(tr => {
                  const s = Math.max(MIN_SCALE, Math.min(MAX_SCALE, tr.scale * factor));
                  const r = s / tr.scale;
                  return {scale: s, x: mx - r * (mx - tr.x), y: my - r * (my - tr.y)};
               });
            }
         }
         lastPinchDist.current = dist;
      }
   }, [dragging, dragStart]);
   
   const onTouchEnd = useCallback((e: React.TouchEvent) => {
      lastPinchDist.current = null;
      if (e.touches.length === 0) setDragging(false);
      touchesRef.current = Array.from(e.touches) as unknown as React.Touch[];
   }, []);
   
   // ── Zoom button helper ────────────────────────────────────────────────────────
   const zoomBy = (factor: number) => setTransform(t => {
      const s = Math.max(MIN_SCALE, Math.min(MAX_SCALE, t.scale * factor));
      const r = s / t.scale;
      return {scale: s, x: dims.w / 2 - r * (dims.w / 2 - t.x), y: dims.h / 2 - r * (dims.h / 2 - t.y)};
   });
   
   // ── Node sizing ───────────────────────────────────────────────────────────────
   const maxPop = useMemo(() => Math.max(...nodes.map(n => n.population || 1), 1), [nodes]);
   const nodeR = (pop: number) => Math.max(5, Math.sqrt(pop / maxPop) * 24);
   
   // ── Style helpers ─────────────────────────────────────────────────────────────
   const statusFill = (s?: string) => s === 'rich' ? '#78350f' : s === 'middle' ? '#1e3a5f' : '#1f2937';
   const statusStroke = (s?: string) => s === 'rich' ? '#f59e0b' : s === 'middle' ? '#60a5fa' : '#6b7280';
   const stabColor = (v: number) => v > 0.65 ? '#10b981' : v > 0.35 ? '#f59e0b' : '#ef4444';
   
   // ── Resource type colours ─────────────────────────────────────────────────────
   const rnColor = (type: string) => ({
      oil: {stroke: '#f59e0b', fill: 'rgba(245,158,11,0.06)', dot: '#f59e0b'},
      minerals: {stroke: '#a78bfa', fill: 'rgba(167,139,250,0.06)', dot: '#a78bfa'},
      forest: {stroke: '#34d399', fill: 'rgba(52,211,153,0.06)', dot: '#34d399'},
      water: {stroke: '#38bdf8', fill: 'rgba(56,189,248,0.06)', dot: '#38bdf8'},
      gas: {stroke: '#fb923c', fill: 'rgba(251,146,60,0.06)', dot: '#fb923c'},
      generic: {stroke: '#94a3b8', fill: 'rgba(148,163,184,0.06)', dot: '#94a3b8'},
   })[type] ?? {stroke: '#94a3b8', fill: 'rgba(148,163,184,0.06)', dot: '#94a3b8'};
   
   const rnIcon = (type: string): React.ReactNode =>
      ({
         oil: <Fuel className="w-4 h-4" />,
         minerals: <Pickaxe className="w-4 h-4" />,
         forest: <Trees className="w-4 h-4" />,
         water: <Droplet className="w-4 h-4" />,
         gas: <Flame className="w-4 h-4" />,
      } as Record<string, React.ReactNode>)[type] ?? <span>◆</span>;
   
   // ── Summary stats ─────────────────────────────────────────────────────────────
   const stats = useMemo(() => {
      const totalPop = nodes.reduce((s, n) => s + (n.population || 0), 0);
      const avgStability = nodes.length ? nodes.reduce((s, n) => s + (n.stability || 0), 0) / nodes.length : 0;
      const tradingConns = connections.filter(c => (Number(c.last_trade_volume) || 0) > 0).length;
      const criticalNodes = nodes.filter(n => (n.stability || 0) < 0.25).length;
      return {totalPop, avgStability, tradingConns, criticalNodes};
   }, [nodes, connections]);
   
   // ── Minimap viewport rect ─────────────────────────────────────────────────────
   const minimapRect = useMemo(() => {
      const {x, y, scale} = transform;
      const wx = -x / scale, wy = -y / scale;
      const ww = dims.w / scale, wh = dims.h / scale;
      return {
         mx: ((wx - bounds.minX) / worldW) * MINIMAP_W,
         my: ((wy - bounds.minY) / worldH) * MINIMAP_H,
         mw: (ww / worldW) * MINIMAP_W,
         mh: (wh / worldH) * MINIMAP_H,
      };
   }, [transform, dims, bounds, worldW, worldH]);
   
   const hoveredNodeData = hoveredNode != null ? nodes.find(n => n.id === hoveredNode) : null;
   const hoveredConnData = hoveredConn != null ? connections.find(c => c.id === hoveredConn) : null;
   
   const showLabels = transform.scale > 0.38;
   const showDetails = transform.scale > 0.85;
   const zoomPct = Math.round(transform.scale * 100);
   
   return (
      <div className="flex flex-col gap-3">
         
         {/* ── KPI bar ── */}
         <div className="grid grid-cols-2 md:grid-cols-4 gap-3 shrink-0">
            <KpiCard icon={<Globe className="w-4 h-4" />} label="Nodes" value={nodes.length} color="text-white"
                     sub={stats.criticalNodes > 0 ? `${stats.criticalNodes} critical` : undefined} />
            <KpiCard icon={<Users className="w-4 h-4" />} label="Total Population" value={fmtValue(stats.totalPop)} color="text-blue-400" />
            <KpiCard icon={<ChartColumn className="w-4 h-4" />} label="Avg Stability"
                     value={`${(stats.avgStability * 100).toFixed(1)}%`}
                     color={stats.avgStability > 0.65 ? 'text-green-400' : stats.avgStability > 0.40 ? 'text-yellow-400' : stats.avgStability > 0.20 ? 'text-orange-400' : 'text-red-400'} />
            <KpiCard icon={<Dot color="#c084fc" />} label="Active Trades" value={stats.tradingConns} color="text-purple-400"
                     sub={`${connections.length} total routes`} />
         </div>
         
         {/* ── Map canvas ── */}
         <div ref={containerRef}
              className="relative grow-1 shrink-1 h-[calc(100vh-20rem)] bg-gray-900 border border-gray-800 rounded-xl overflow-hidden select-none touch-none">
            
            <svg ref={svgRef}
                 width={dims.w} height={dims.h}
                 className={`absolute inset-0 ${dragging ? 'cursor-grabbing' : 'cursor-grab'}`}
                 onWheel={handleWheel}
                 onMouseDown={onMouseDown}
                 onMouseMove={onMouseMove}
                 onMouseUp={onMouseUp}
                 onMouseLeave={onMouseUp}
                 onTouchStart={onTouchStart}
                 onTouchMove={onTouchMove}
                 onTouchEnd={onTouchEnd}>
               
               <defs>
                  <pattern id="world-dots" x="0" y="0" width={40} height={40}
                           patternUnits="userSpaceOnUse"
                           patternTransform={`translate(${transform.x % 40} ${transform.y % 40})`}>
                     <circle cx="20" cy="20" r="0.7" fill="rgba(255,255,255,0.05)" />
                  </pattern>
                  <filter id="glow-purple" x="-60%" y="-60%" width="220%" height="220%">
                     <feGaussianBlur stdDeviation="4" result="b" />
                     <feMerge>
                        <feMergeNode in="b" />
                        <feMergeNode in="SourceGraphic" />
                     </feMerge>
                  </filter>
                  <filter id="glow-node" x="-80%" y="-80%" width="260%" height="260%">
                     <feGaussianBlur stdDeviation="6" result="b" />
                     <feMerge>
                        <feMergeNode in="b" />
                        <feMergeNode in="SourceGraphic" />
                     </feMerge>
                  </filter>
                  <filter id="glow-crit" x="-60%" y="-60%" width="220%" height="220%">
                     <feGaussianBlur stdDeviation="5" result="b" />
                     <feMerge>
                        <feMergeNode in="b" />
                        <feMergeNode in="SourceGraphic" />
                     </feMerge>
                  </filter>
                  <style>{`
              @keyframes flow  { to { stroke-dashoffset: -20; } }
              @keyframes pulse { 0%,100% { opacity:.15; } 50% { opacity:.45; } }
              .conn-traffic { animation: flow 2s linear infinite; }
              .conn-trade   { animation: flow 0.9s linear infinite; }
              .crit-pulse   { animation: pulse 1.4s ease-in-out infinite; }
            `}</style>
               </defs>
               
               <rect width={dims.w} height={dims.h} fill="url(#world-dots)" />
               
               {/* ── World transform group ── */}
               <g transform={`translate(${transform.x} ${transform.y}) scale(${transform.scale})`}>
                  
                  {/* Connections */}
                  {connections.map(conn => {
                     const nA = nodes.find(n => n.id === conn.node_a_id);
                     const nB = nodes.find(n => n.id === conn.node_b_id);
                     if (!nA || !nB) return null;
                     const isTrading = (Number(conn.last_trade_volume) || 0) > 0;
                     const traffic = Number(conn.traffic_volume) || 0;
                     const isHov = hoveredConn === conn.id;
                     const isNodeHov = hoveredNode === conn.node_a_id || hoveredNode === conn.node_b_id;
                     const active = isHov || isNodeHov;
                     const dx = nB.x - nA.x, dy = nB.y - nA.y;
                     const cx = (nA.x + nB.x) / 2 - dy * 0.12;
                     const cy = (nA.y + nB.y) / 2 + dx * 0.12;
                     const d = `M ${nA.x} ${nA.y} Q ${cx} ${cy} ${nB.x} ${nB.y}`;
                     const baseW = Math.max(0.4, Math.min(5, 0.6 + traffic / 100));
                     const opacity = active ? 1 : Math.min(0.85, 0.12 + traffic / 100);
                     const color = isTrading ? `rgba(168,85,247,${opacity})` : `rgba(96,165,250,${opacity})`;
                     return (
                        <g key={conn.id}>
                           <path d={d} fill="none" stroke="transparent"
                                 strokeWidth={Math.max(14 / transform.scale, 8)}
                                 style={{cursor: 'pointer'}}
                                 onMouseEnter={() => setHoveredConn(conn.id)}
                                 onMouseLeave={() => setHoveredConn(null)}
                                 onClick={e => {
                                    e.stopPropagation();
                                    setSelectedConnectionId(conn.id);
                                 }} />
                           <path d={d} fill="none" stroke={color} strokeWidth={baseW}
                                 strokeDasharray={traffic > 0 || isTrading ? '7 5' : undefined}
                                 className={isTrading ? 'conn-trade' : traffic > 0 ? 'conn-traffic' : undefined}
                                 filter={active && isTrading ? 'url(#glow-purple)' : undefined}
                                 style={{pointerEvents: 'none'}} />
                           {active && (
                              <path d={d} fill="none"
                                    stroke={isTrading ? 'rgba(216,180,254,0.35)' : 'rgba(147,197,253,0.25)'}
                                    strokeWidth={baseW + 3} style={{pointerEvents: 'none'}} />
                           )}
                        </g>
                     );
                  })}
                  
                  {/* Nodes */}
                  {nodes.map(node => {
                     const r = nodeR(node.population || 0);
                     const isHov = hoveredNode === node.id;
                     const isCrit = (node.stability || 0) < 0.25;
                     const isTrading = connections.some(c =>
                        (c.node_a_id === node.id || c.node_b_id === node.id) &&
                        (Number(c.last_trade_volume) || 0) > 0
                     );
                     const stab = node.stability || 0;
                     const circ = 2 * Math.PI * r;
                     const sColor = stabColor(stab);
                     return (
                        <g key={node.id} style={{cursor: 'pointer'}}
                           onClick={() => setSelectedNodeId(node.id)}
                           onMouseEnter={() => setHoveredNode(node.id)}
                           onMouseLeave={() => setHoveredNode(null)}>
                           {isCrit && (
                              <circle cx={node.x} cy={node.y} r={r + 8}
                                      fill="rgba(239,68,68,0.18)" stroke="none"
                                      filter="url(#glow-crit)" className="crit-pulse" />
                           )}
                           {isTrading && (
                              <circle cx={node.x} cy={node.y} r={r + 5}
                                      fill="rgba(168,85,247,0.07)"
                                      stroke="rgba(168,85,247,0.2)" strokeWidth={1.2} />
                           )}
                           <circle cx={node.x} cy={node.y} r={r}
                                   fill={statusFill(node.income_status)}
                                   stroke={isHov ? '#f8fafc' : statusStroke(node.income_status)}
                                   strokeWidth={isHov ? 2.5 : 1.5}
                                   filter={isHov ? 'url(#glow-node)' : undefined} />
                           <circle cx={node.x} cy={node.y} r={r} fill="none"
                                   stroke={sColor} strokeWidth={2.5}
                                   strokeDasharray={`${stab * circ} ${circ}`}
                                   strokeDashoffset={circ * 0.25} opacity={0.9}
                                   style={{pointerEvents: 'none'}} />
                           {showLabels && (
                              <text x={node.x} y={node.y + r + Math.max(9, 10 / transform.scale)}
                                    textAnchor="middle" fontSize={Math.max(8, 11 / transform.scale)}
                                    fill={isCrit ? '#fca5a5' : '#e5e7eb'}
                                    style={{pointerEvents: 'none', fontFamily: 'system-ui,sans-serif'}}>
                                 {node.name}
                              </text>
                           )}
                           {showDetails && r > 10 && (
                              <text x={node.x} y={node.y + Math.max(4, 4 / transform.scale)}
                                    textAnchor="middle" fontSize={Math.max(6, 8 / transform.scale)}
                                    fill="rgba(255,255,255,0.55)"
                                    style={{pointerEvents: 'none', fontFamily: 'monospace'}}>
                                 {fmtValue(node.population || 0)}
                              </text>
                           )}
                        </g>
                     );
                  })}
                  
                  {/* Investment lines — dashed links from investing nodes to resource nodes */}
                  {resourceNodes.map(rn => {
                     const c = rnColor(rn.type);
                     const rnInvs = investments.filter(inv => inv.resource_id === rn.id);
                     const maxSh = rn.max_total_shares ?? 100;
                     return rnInvs.map(inv => {
                        const node = nodes.find(n => n.id === inv.node_id);
                        if (!node) return null;
                        const sharePct = inv.shares / Math.max(maxSh, 1);
                        const strokeW = Math.max(0.4, sharePct * 3);
                        return (
                           <line key={`inv-${inv.node_id}-${rn.id}`}
                                 x1={node.x} y1={node.y} x2={rn.x} y2={rn.y}
                                 stroke={c.stroke} strokeWidth={strokeW}
                                 strokeDasharray="4 3" opacity={0.35 + sharePct * 0.4}
                                 className="pointer-events-none" />
                        );
                     });
                  })}

                  {/* Resource node radius zones */}
                  {resourceNodes.map(rn => {
                     const c = rnColor(rn.type);
                     const isHov = hoveredNode === -rn.id;
                     const rnInvs = investments.filter(inv => inv.resource_id === rn.id);
                     const maxSh = rn.max_total_shares ?? 100;
                     const totalSold = rnInvs.reduce((s, inv) => s + inv.shares, 0);
                     const satPct = Math.min(1, totalSold / Math.max(maxSh, 1));
                     const dotR = 7;
                     // Arc fill for saturation: sweep from top clockwise
                     const angle = satPct * 2 * Math.PI;
                     const arcX = rn.x + dotR * Math.sin(angle);
                     const arcY = rn.y - dotR * Math.cos(angle);
                     const largeArc = angle > Math.PI ? 1 : 0;
                     const arcPath = satPct >= 0.999
                        ? `M ${rn.x} ${rn.y - dotR} A ${dotR} ${dotR} 0 1 1 ${rn.x - 0.001} ${rn.y - dotR} Z`
                        : satPct <= 0
                        ? ''
                        : `M ${rn.x} ${rn.y} L ${rn.x} ${rn.y - dotR} A ${dotR} ${dotR} 0 ${largeArc} 1 ${arcX} ${arcY} Z`;
                     return (
                        <g key={`rn-${rn.id}`}>
                           <circle cx={rn.x} cy={rn.y} r={rn.radius}
                                   fill={c.fill} stroke={c.stroke}
                                   strokeWidth={0.8} strokeDasharray="6 4"
                                   opacity={isHov ? 1 : 0.6}
                                   className="pointer-events-none" />
                           <circle cx={rn.x} cy={rn.y} r={dotR}
                                   fill="#111827" stroke={c.stroke} strokeWidth={2}
                                   style={{cursor: 'pointer'}}
                                   onMouseEnter={() => setHoveredNode(-rn.id)}
                                   onMouseLeave={() => setHoveredNode(null)}
                                   onClick={() => setSelectedResourceNodeId(rn.id)} />
                           {arcPath && (
                              <path d={arcPath} fill={c.stroke} opacity={0.55}
                                    className="pointer-events-none" />
                           )}
                           {showLabels && (
                              <>
                                 <text x={rn.x} y={rn.y + 7 + Math.max(9, 11 / transform.scale)}
                                       textAnchor="middle" fontSize={Math.max(6, 9 / transform.scale)}
                                       fill={c.stroke}
                                       style={{pointerEvents: 'none', fontFamily: 'system-ui,sans-serif'}}>
                                    {rn.name}
                                 </text>
                              </>
                           )}
                        </g>
                     );
                  })}
               </g>
            </svg>
            
            {/* ── Legend ── */}
            <div className="absolute top-3 left-3 bg-gray-950/90 border border-gray-700 rounded-xl text-xs pointer-events-auto">
               <button
                  onClick={() => setLegendOpen(o => !o)}
                  className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white transition-colors w-full"
               >
                  <span className="text-[10px] uppercase tracking-wider">Legend</span>
                  <span className="ml-auto text-gray-600 text-[10px]">{legendOpen ? '▲' : '▼'}</span>
               </button>
               
               {legendOpen && (
                  <div className="px-3 pb-2.5 space-y-1.5 border-t border-gray-800">
                     {[
                        {color: '#f59e0b', bg: '#78350f', label: 'Rich'},
                        {color: '#60a5fa', bg: '#1e3a5f', label: 'Middle'},
                        {color: '#6b7280', bg: '#1f2937', label: 'Poor'},
                     ].map(({color, bg, label}) => (
                        <div key={label} className="flex items-center gap-2 mt-1.5">
                           <svg width="12" height="12">
                              <circle cx="6" cy="6" r="5" fill={bg} stroke={color} strokeWidth="1.5" />
                           </svg>
                           <span className="text-gray-400">{label}</span>
                        </div>
                     ))}
                     <div className="border-t border-gray-800 pt-1.5 space-y-1.5">
                        <div className="flex items-center gap-2">
                           <svg width="20" height="6">
                              <line x1="0" y1="3" x2="20" y2="3" stroke="rgba(168,85,247,0.8)" strokeWidth="2" strokeDasharray="4 3" />
                           </svg>
                           <span className="text-gray-400">Trading</span>
                        </div>
                        <div className="flex items-center gap-2">
                           <svg width="20" height="6">
                              <line x1="0" y1="3" x2="20" y2="3" stroke="rgba(96,165,250,0.6)" strokeWidth="1.5" strokeDasharray="4 3" />
                           </svg>
                           <span className="text-gray-400">Traffic</span>
                        </div>
                        <div className="flex items-center gap-2">
                           <svg width="20" height="8">
                              <circle cx="10" cy="4" r="3.5" fill="#1f2937" stroke="#ef4444" strokeWidth="1.2" />
                           </svg>
                           <span className="text-gray-400">Critical</span>
                        </div>
                        <div className="flex items-center gap-2">
                           <svg width="12" height="12">
                              <circle cx="6" cy="6" r="5" fill="#111827" stroke="#a78bfa" strokeWidth="1.5" strokeDasharray="3 2" />
                           </svg>
                           <span className="text-gray-400">Resource zone</span>
                        </div>
                     </div>
                  </div>
               )}
            </div>
            
            {/* ── Zoom controls ── */}
            <div className="absolute bottom-3 left-3 flex flex-col gap-1">
               {[
                  {label: '+', action: () => zoomBy(1.3)},
                  {label: '−', action: () => zoomBy(0.77)},
                  {label: '⤢', action: fitToScreen, title: 'Fit all'},
               ].map(({label, action, title}) => (
                  <button key={label} onClick={action} title={title}
                          className="w-8 h-8 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm hover:bg-gray-700 active:bg-gray-600 flex items-center justify-center transition-colors">
                     {label}
                  </button>
               ))}
            </div>
            
            {/* Zoom % */}
            <div className="absolute top-3 right-3 bg-gray-950/80 border border-gray-700 rounded-lg px-2 py-1 text-xs font-mono text-gray-400 pointer-events-none">
               {zoomPct}%
            </div>
            
            {/* ── Minimap ── */}
            <div className="absolute bottom-3 right-3 bg-gray-950/95 border border-gray-700 rounded-xl overflow-hidden"
                 style={{width: MINIMAP_W, height: MINIMAP_H}}>
               <div className="absolute top-0 left-0 right-0 text-[9px] text-gray-600 uppercase tracking-wider px-1.5 pt-1 pointer-events-none">Overview</div>
               <svg width={MINIMAP_W} height={MINIMAP_H}>
                  {connections.map(conn => {
                     const nA = nodes.find(n => n.id === conn.node_a_id);
                     const nB = nodes.find(n => n.id === conn.node_b_id);
                     if (!nA || !nB) return null;
                     const ax = ((nA.x - bounds.minX) / worldW) * MINIMAP_W;
                     const ay = ((nA.y - bounds.minY) / worldH) * MINIMAP_H;
                     const bx = ((nB.x - bounds.minX) / worldW) * MINIMAP_W;
                     const by = ((nB.y - bounds.minY) / worldH) * MINIMAP_H;
                     const isT = (Number(conn.last_trade_volume) || 0) > 0;
                     return (
                        <line key={conn.id} x1={ax} y1={ay} x2={bx} y2={by}
                              stroke={isT ? 'rgba(168,85,247,0.2)' : 'rgba(96,165,250,0.05)'}
                              strokeWidth={isT ? 0.8 : 0.4} />
                     );
                  })}
                  {nodes.map(node => {
                     const mx = ((node.x - bounds.minX) / worldW) * MINIMAP_W;
                     const my = ((node.y - bounds.minY) / worldH) * MINIMAP_H;
                     return <circle key={node.id} cx={mx} cy={my} r={2.2} fill={statusStroke(node.income_status)} opacity={0.85} />;
                  })}
                  {resourceNodes.map(rn => {
                     const mx = ((rn.x - bounds.minX) / worldW) * MINIMAP_W;
                     const my = ((rn.y - bounds.minY) / worldH) * MINIMAP_H;
                     const mr = (rn.radius / worldW) * MINIMAP_W;
                     const c = rnColor(rn.type);
                     return (
                        <g key={`mrn-${rn.id}`}>
                           <circle cx={mx} cy={my} r={mr} fill="none" stroke={c.stroke} strokeWidth={0.5} opacity={0.4} strokeDasharray="2 2" />
                           <circle cx={mx} cy={my} r={2} fill={c.dot} opacity={0.9} />
                        </g>
                     );
                  })}
                  <rect x={Math.max(0, minimapRect.mx)} y={Math.max(0, minimapRect.my)}
                        width={Math.min(MINIMAP_W, minimapRect.mw)} height={Math.min(MINIMAP_H, minimapRect.mh)}
                        fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.45)" strokeWidth={1} rx={1} />
               </svg>
            </div>
            
            {/* ── Node hover card — hidden on touch devices ── */}
            {hoveredNodeData && (() => {
               const rect = svgRef.current?.getBoundingClientRect();
               if (!rect) return null;
               const px = mousePos.x - rect.left;
               const py = mousePos.y - rect.top;
               const flipX = px > dims.w * 0.62;
               const flipY = py > dims.h * 0.6;
               const nConns = connections.filter(c => c.node_a_id === hoveredNodeData.id || c.node_b_id === hoveredNodeData.id);
               const income = Object.values(hoveredNodeData.income_sources || {}).reduce((a: number, b: any) => a + Number(b || 0), 0);
               const isCrit = (hoveredNodeData.stability || 0) < 0.25;
               return (
                  <div className="absolute pointer-events-none bg-gray-950 border border-gray-700 rounded-xl p-3.5 shadow-2xl w-52 z-10 hidden md:block"
                       style={{left: flipX ? px - 224 : px + 16, top: flipY ? py - 210 : py + 16}}>
                     <div className="flex items-center justify-between mb-2.5">
                     <span className={`font-semibold text-sm flex items-center gap-1 ${isCrit ? 'text-red-300' : 'text-white'}`}>
                        {isCrit && <TriangleAlert className="w-3.5 h-3.5 shrink-0" />}{hoveredNodeData.name}
                     </span>
                        <StatusBadge preset={hoveredNodeData.income_status as BadgePreset}>{hoveredNodeData.income_status}</StatusBadge>
                  </div>
                  <div className="space-y-1.5">
                     {([
                        ['Population', fmtValue(hoveredNodeData.population || 0), 'text-blue-400'],
                        ['Income', `+${fmtValue(income)}`, 'text-emerald-400'],
                        ['Resources', fmtValue(hoveredNodeData.resources || 0), 'text-green-400'],
                        ['Stability', `${((hoveredNodeData.stability || 0) * 100).toFixed(1)}%`, isCrit ? 'text-red-400' : (hoveredNodeData.stability || 0) > 0.6 ? 'text-green-400' : 'text-yellow-400'],
                        ['Emissions', fmtValue(hoveredNodeData.emissions || 0), (hoveredNodeData.emissions || 0) > 35000 ? 'text-red-400' : 'text-gray-300'],
                        ['Connections', `${nConns.length} routes`, 'text-gray-300'],
                     ] as [string, string, string][]).map(([label, value, color]) => (
                        <div key={label} className="flex justify-between items-center">
                           <span className="text-xs text-gray-500">{label}</span>
                           <span className={`text-xs font-mono font-medium ${color}`}>{value}</span>
                        </div>
                     ))}
                  </div>
                     <div className="mt-2 pt-2 border-t border-gray-800 text-[10px] text-gray-600">Click to open details</div>
                  </div>
               );
            })()}
            
            {/* Resource node hover card — hidden on touch devices */}
            {hoveredNode != null && hoveredNode < 0 && (() => {
               const rn = resourceNodes.find(r => r.id === -hoveredNode!);
               if (!rn) return null;
               const rect = svgRef.current?.getBoundingClientRect();
               if (!rect) return null;
               const px = mousePos.x - rect.left;
               const py = mousePos.y - rect.top;
               const c = rnColor(rn.type);
               const nodesInRange = nodes.filter(n => Math.sqrt((n.x - rn.x) ** 2 + (n.y - rn.y) ** 2) <= rn.radius);
               const rnInvs = investments.filter(inv => inv.resource_id === rn.id);
               const maxSh = rn.max_total_shares ?? 100;
               const totalSold = rnInvs.reduce((s, inv) => s + inv.shares, 0);
               const bonusTotal = rnInvs.reduce((s, inv) => s + 1000 * Math.sqrt(inv.shares / Math.max(maxSh, 1)), 0);
               const totalYield = nodesInRange.length * 100 + bonusTotal;
               const topInvs = [...rnInvs].sort((a, b) => b.shares - a.shares).slice(0, 3);
               return (
                  <div className="absolute pointer-events-none bg-gray-950 border border-gray-700 rounded-xl p-3.5 shadow-2xl w-56 z-10 hidden md:block"
                       style={{left: px + 16, top: py + 16}}>
                     <div className="flex items-center gap-2 mb-2.5">
                        <span className="text-lg flex items-center">{rnIcon(rn.type)}</span>
                        <div>
                           <div className="text-sm font-semibold text-white">{rn.name}</div>
                           <div className="text-xs capitalize" style={{color: c.stroke}}>{rn.type} deposit</div>
                        </div>
                     </div>
                     <div className="space-y-1.5">
                        {([
                           ['Total yield/tick', `+${Math.round(totalYield)}`, 'text-emerald-400'],
                           ['Base yield', `+${nodesInRange.length * 100}`, 'text-gray-300'],
                           ['Investor bonus', `+${Math.round(bonusTotal)}`, 'text-violet-400'],
                           ['Shares sold', `${totalSold}/${maxSh}`, totalSold > 0 ? 'text-violet-300' : 'text-gray-500'],
                           ['Nodes in range', `${nodesInRange.length}`, nodesInRange.length > 0 ? 'text-green-400' : 'text-gray-500'],
                        ] as [string, string, string][]).map(([label, value, color]) => (
                           <div key={label} className="flex justify-between">
                              <span className="text-xs text-gray-500">{label}</span>
                              <span className={`text-xs font-mono ${color}`}>{value}</span>
                           </div>
                        ))}
                     </div>
                     {topInvs.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-800">
                           <div className="text-[10px] text-gray-500 mb-1">Top investors:</div>
                           {topInvs.map(inv => {
                              const n = nodes.find(nd => nd.id === inv.node_id);
                              return (
                                 <div key={inv.node_id} className="flex justify-between text-xs">
                                    <span className="text-gray-300 truncate max-w-[120px]">{n?.name ?? `#${inv.node_id}`}</span>
                                    <span className="font-mono text-violet-300">{inv.shares} sh</span>
                                 </div>
                              );
                           })}
                        </div>
                     )}
                  </div>
               );
            })()}
            
            {/* ── Connection hover card — hidden on touch devices ── */}
            {hoveredConnData && !hoveredNodeData && (() => {
               const rect = svgRef.current?.getBoundingClientRect();
               if (!rect) return null;
               const px = mousePos.x - rect.left;
               const py = mousePos.y - rect.top;
               const nA = nodes.find(n => n.id === hoveredConnData.node_a_id);
               const nB = nodes.find(n => n.id === hoveredConnData.node_b_id);
               const isT = (Number(hoveredConnData.last_trade_volume) || 0) > 0;
               return (
                  <div className="absolute pointer-events-none bg-gray-950 border border-gray-700 rounded-xl p-3 shadow-2xl w-48 z-10 hidden md:block"
                       style={{left: px + 14, top: py + 14}}>
                     <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Connection</div>
                     <div className="text-sm text-white font-medium mb-2">
                        {nA?.name ?? `#${hoveredConnData.node_a_id}`} ↔ {nB?.name ?? `#${hoveredConnData.node_b_id}`}
                     </div>
                     <div className="space-y-1">
                        {([
                           ['Traffic', fmtValue(Number(hoveredConnData.traffic_volume) || 0), 'text-blue-400'],
                           ['Maint', `−${(Number(hoveredConnData.maintenance_cost) || 0).toFixed(0)}`, 'text-red-400'],
                           ...(isT ? [
                              ['Trade vol', `${(Number(hoveredConnData.last_trade_volume) || 0).toFixed(1)} res`, 'text-purple-400'],
                              ['Trade val', `+${fmtValue(Number(hoveredConnData.last_trade_value) || 0)}`, 'text-green-400'],
                           ] : []),
                        ] as [string, string, string][]).map(([label, value, color]) => (
                           <div key={label} className="flex justify-between">
                              <span className="text-xs text-gray-500">{label}</span>
                              <span className={`text-xs font-mono ${color}`}>{value}</span>
                           </div>
                        ))}
                     </div>
                     <div className="mt-2 pt-2 border-t border-gray-800 text-[10px] text-gray-600">Click to inspect
                     </div>
                  </div>
               );
            })()}
            
            {nodes.length === 0 && (
               <div className="absolute inset-0 flex items-center justify-center text-gray-600 text-sm">
                  No nodes yet.
               </div>
            )}
         </div>
         
         {/* ── Footer hint ── */}
         <p className="text-xs text-gray-600 shrink-0 px-1">
            <span className="hidden md:inline">Scroll to zoom · Drag to pan · Click any node or route to inspect</span>
            <span className="md:hidden">Pinch to zoom · Drag to pan · Tap any node or route to inspect</span>
         </p>
      </div>
   );
};

export default SpatialNodesView;
